"use client";

import { OffersOffer, PaginationType } from '@/services/dataTypes';
import React, { useEffect, useState } from 'react';
import { filterOfferBanners, getBannerDimensions } from '@/constants/hooks';
import { apiOfferBanners } from '@/apis/offers';
import Banner from './Banner';

interface Props {
    bannerResponse: OffersOffer[];
    domain: string;
    mer_slug: string;
    slug_type: string;
    merchantId: string;
    companyId: string;
    pagination: PaginationType;
}

const VerticalOfferBanner = ({ bannerResponse, domain, mer_slug, slug_type, merchantId, companyId, pagination }: Props) => {
    const [banners, setBanners] = useState<OffersOffer[]>([]);
    const [page, setPage] = useState(2);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);

    useEffect(() => {
        setBanners(filterOfferBanners(bannerResponse, 20, 2000, 20, 2000));
        if (pagination?.next_page === null) setHasMore(false);
    }, [bannerResponse, pagination]);

    const loadMore = async () => {
        if (loading || !hasMore) return;
        setLoading(true);
        try {
            const res = await apiOfferBanners(merchantId, companyId, page);
            const newBanners = filterOfferBanners(res.data?.offers || [], 20, 2000, 20, 2000);
            setBanners(prev => [...prev, ...newBanners]);
            if (res?.data?.pagination?.next_page) {
                setPage(p => p + 1);
                setIsExpanded(true);
            } else {
                setHasMore(false);
            }
        } catch {
            /* silent */
        } finally {
            setLoading(false);
        }
    };

    const showLess = () => {
        setBanners(filterOfferBanners(bannerResponse, 20, 2000, 20, 2000));
        setPage(2);
        setHasMore(true);
        setIsExpanded(false);
    };

    if (!banners?.length) return null;

    return (
        <div
            className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
        >
            {/* Header */}
            <div className="px-5 py-4 bg-gray-50 border-b border-gray-100">
                <h4 className="text-sm font-bold text-gray-800 m-0">
                    Featured Offers
                </h4>
                <p className="text-[10px] text-gray-500 m-0 mt-0.5">Exclusive banner deals</p>
            </div>

            {/* Banners */}
            <div className="p-4 flex flex-col gap-3">
                {banners.map((offer_data, i) => {
                    const dimension = getBannerDimensions(offer_data);
                    return (
                        <div
                            key={i}
                            className="overflow-hidden rounded-xl border border-gray-100 transition-all duration-300 hover:shadow-md hover:-translate-y-0.5"
                        >
                            <Banner
                                data={offer_data}
                                height={dimension?.height}
                                mer_slug={mer_slug}
                                slug_type={slug_type}
                                domain={domain}
                                width={dimension?.width}
                            />
                        </div>
                    );
                })}
            </div>

            {/* Controls */}
            {(hasMore || isExpanded) && (
                <div className="px-4 pb-4 flex items-center gap-2">
                    {hasMore && (
                        <button
                            onClick={loadMore}
                            disabled={loading}
                            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-[11px] font-semibold text-white transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                            style={{ background: '#5D27A1' }}
                        >
                            {loading ? (
                                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            ) : (
                                'Load More'
                            )}
                        </button>
                    )}
                    {isExpanded && (
                        <button
                            onClick={showLess}
                            className="flex-1 py-2.5 rounded-xl text-[11px] font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 transition-all duration-200"
                        >
                            Show Less
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default VerticalOfferBanner;
